import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaCog, FaSignOutAlt, FaTimes } from 'react-icons/fa';
import { MdDashboard, MdPerson, MdPeople, MdAddBox, MdMoney, MdLocalHospital, MdLibraryBooks } from 'react-icons/md';
import '../assets/css/mainstyle.css';
import '../assets/css/ClientSidebar.css';
import medocImage from '../assets/images/medocplastics.png';
import logomedtinz from '../assets/images/Bright Web.png';
import logoclinic from '../assets/images/logoclinic.png';
import api from '../service/caller';
import { usePermissions } from '../contexts/PermissionContext';
import { FaLock } from "react-icons/fa";
import { AiTwotoneDashboard } from "react-icons/ai";
import { RiListSettingsLine } from "react-icons/ri";
import { HiMiniUsers } from "react-icons/hi2";
import { GrUserSettings } from "react-icons/gr";
import { AiTwotoneSchedule } from "react-icons/ai";
import { FaMoneyBills } from "react-icons/fa6";
import { BsHospital } from "react-icons/bs";
import { ImLab } from "react-icons/im";
import { TbPresentationAnalytics } from "react-icons/tb";
import { RiMedicineBottleFill, RiCalendarScheduleLine } from "react-icons/ri";
import { FaBed } from "react-icons/fa6";

const ClientSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { UserData, hasPermission } = usePermissions();
  const [role, setRole] = useState('');
  const [activeItem, setActiveItem] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (UserData) {
      setRole(UserData.role?.name || '');
    }
  }, [UserData]);
  
  // Set active menu item based on current path
  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  // Check if screen is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 991);
      // Auto close sidebar on mobile when resizing
      if (window.innerWidth <= 991) {
        setSidebarOpen(false);
      }
    };
    
    // Initialize on component mount
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const handlePatientFileRedirect = async () => {
    const patientId = UserData?._id;
    console.log('UserData ID:', patientId);

    if (patientId) {
      try {
        const response = await api.get(`clinic/patientsbyuserid/${patientId}`);
        const patientData = response.data;

        if (patientData && patientData._id) {
          const path = `/hospitaladmin/patientfilemedical/${patientData._id}`;
          setActiveItem(path);
          navigate(path);
          if (isMobile) setSidebarOpen(false);
        } else {
          console.log("Aucun patient trouvé avec cet ID");
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données du patient', error);
      }
    } else {
      console.log('Aucun ID d\'utilisateur trouvé');
    } 
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm('Voulez-vous vous déconnecter?');
    if (confirmLogout) {
      localStorage.removeItem('token');
      localStorage.removeItem('access_token');
      localStorage.clear();
      navigate('/Clientlogin');
    }
  };
  
  const handleMenuClick = (path) => {
    setActiveItem(path);
    if (isMobile) setSidebarOpen(false); // Close sidebar on mobile after menu item click
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Should text be shown in menu items
  const showText = sidebarOpen || isMobile;

  return (
    <>
      {/* Mobile toggle button outside the sidebar */}
      {isMobile && !sidebarOpen && (
        <div className="mobile-sidebar-toggle" onClick={toggleSidebar}>
          <FaBars />
        </div>
      )}
      
      <div className={`sidebar-container ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className='sidebartop d-flex align-items-center justify-content-center'>
            <img src={UserData?.hospital_id[0]?.picture || logoclinic} className='cardicon sidebar-logo me-auto' alt="Consultation Icon" style={{ width: 'auto', height: '80px' }} />
            <div className=''>
              {isMobile ? (
                <FaTimes onClick={toggleSidebar} className='sidebar-toggle-btn' />
              ) : (
                <FaBars onClick={toggleSidebar} className='sidebar-toggle-btn' />
              )}
            </div>
          </div>
        </div>

        <div className={`sidebar-menu-container ${sidebarOpen ? 'visible' : 'hidden'}`}>
          <div className="sidebar-menu">
            <ul>
              {role !== 'medtinz_client_patient' && (
                <li className={activeItem === '/hospitaladmin/dashboard' ? 'active' : ''}>
                  <Link to="/hospitaladmin/dashboard" onClick={() => handleMenuClick('/hospitaladmin/dashboard')}>
                    <AiTwotoneDashboard className="nav-icon" />
                    {showText && <span>Dashboard</span>}
                  </Link>
                </li>
              )}

              {role === 'medtinz_client_patient' && (
                <li className={activeItem.includes('/hospitaladmin/patientfilemedical/') ? 'active' : ''} onClick={handlePatientFileRedirect}>
                  <Link>
                    <MdPerson className="nav-icon" />
                    {showText && <span>Medical history</span>}
                  </Link>
                </li>
              )}

              {hasPermission('medtinz_client_hospitals_view') && (
                <li className={activeItem === '/hospitaladmin/hospitalsetting' ? 'active' : ''}>
                  <Link to="/hospitaladmin/hospitalsetting" onClick={() => handleMenuClick('/hospitaladmin/hospitalsetting')}>
                    <RiListSettingsLine className="nav-icon" />
                    {showText && <span>Hospital Settings</span>}
                  </Link>
                </li>
              )}
              
              {hasPermission('medtinz_client_hospitals_view') && (
                <li className={activeItem === '/hospitaladmin/locationSetting' ? 'active' : ''}>
                  <Link to="/hospitaladmin/locationSetting" onClick={() => handleMenuClick('/hospitaladmin/locationSetting')}>
                    <BsHospital className="nav-icon" />
                    {showText && <span>Location Settings</span>}
                  </Link>
                </li>
              )}
              
              {hasPermission('medtinz_client_patient_view') && (
                <li className={activeItem === '/hospitaladmin/patientlist' ? 'active' : ''}>
                  <Link to="/hospitaladmin/patientlist" onClick={() => handleMenuClick('/hospitaladmin/patientlist')}>
                    <HiMiniUsers className="nav-icon" />
                    {showText && <span>Patient Management</span>}
                  </Link>
                </li>
              )}

              {role === 'medtinz_client_master' && (
                <li className={activeItem === '/hospitaladmin/rolemanage' ? 'active' : ''}>
                  <Link to="/hospitaladmin/rolemanage" onClick={() => handleMenuClick('/hospitaladmin/rolemanage')}>
                    <GrUserSettings className="nav-icon" />
                    {showText && <span>Role Management</span>}
                  </Link>
                </li>
              )}
              
              {hasPermission('medtinz_client_staff_view') && (
                <li className={activeItem === '/hospitaladmin/stafflist' ? 'active' : ''}>
                  <Link to="/hospitaladmin/stafflist" onClick={() => handleMenuClick('/hospitaladmin/stafflist')}>
                    <GrUserSettings className="nav-icon" />
                    {showText && <span>Staff Management</span>}
                  </Link>
                </li>
              )}
              
              {role !== 'medtinz_client_patient' && (
                <li className={activeItem === '/hospitaladmin/staffschedule' ? 'active' : ''}>
                  <Link to="/hospitaladmin/staffschedule" onClick={() => handleMenuClick('/hospitaladmin/staffschedule')}>
                    <RiCalendarScheduleLine className="nav-icon" />
                    {showText && <span>Schedule Management</span>}
                  </Link>
                </li>
              )}

              {hasPermission('medtinz_client_appointment_view') && (
                <li className={activeItem === '/hospitaladmin/Appointmentlist' ? 'active' : ''}>
                  <Link to="/hospitaladmin/Appointmentlist" onClick={() => handleMenuClick('/hospitaladmin/Appointmentlist')}>
                    <AiTwotoneSchedule className="nav-icon" />
                    {showText && <span>Appointment Scheduling</span>}
                  </Link>
                </li>
              )}

              {role === 'medtinz_client_patient' && (
                <li className={activeItem === '/hospitaladmin/patientPrivacy' ? 'active' : ''}>
                  <Link to="/hospitaladmin/patientPrivacy" onClick={() => handleMenuClick('/hospitaladmin/patientPrivacy')}>
                    <MdPerson className="nav-icon" />
                    {showText && <span>Manage data</span>}
                  </Link>
                </li>
              )}
              
              {hasPermission('medtinz_client_billing_view') && (
                <li className={activeItem === '/Billings/allpaymentlist' ? 'active' : ''}>
                  <Link to="/Billings/allpaymentlist" onClick={() => handleMenuClick('/Billings/allpaymentlist')}>
                    <FaMoneyBills className="nav-icon" />
                    {showText && <span>Billing</span>}
                  </Link>
                </li>
              )}

              {hasPermission('medtinz_client_inventory_pharamacy_view') && (
                <li className="disabled-menu">
                  <Link to="">
                    <RiMedicineBottleFill className="nav-icon" />
                    {showText && <span>Inventory and Pharmacy</span>}
                    {showText && <FaLock className="lock-icon" />}
                  </Link>
                </li>
              )}

              {hasPermission('medtinz_client_laboratory_records_view') && (
                <li className="disabled-menu">
                  <Link to="">
                    <ImLab className="nav-icon" />
                    {showText && <span>Laboratory Records</span>}
                    {showText && <FaLock className="lock-icon" />}
                  </Link>
                </li>
              )}

              {hasPermission('medtinz_client_reports_analytics_view') && (
                <li className={activeItem === '/report' ? 'active' : ''}>
                  <Link to="/report" onClick={() => handleMenuClick('/report')}>
                    <TbPresentationAnalytics className="nav-icon" />
                    {showText && <span>Reports and Analytics</span>}
                  </Link>
                </li>
              )}

              {hasPermission('medtinz_client_room_view') && (
                <li className={activeItem === '/hospitaladmin/rooms_list' ? 'active' : ''}>
                  <Link to="/hospitaladmin/rooms_list" onClick={() => handleMenuClick('/hospitaladmin/rooms_list')}>
                    <FaBed className="nav-icon" />
                    {showText && <span>Room Management</span>}
                  </Link>
                </li>
              )}

              <li className={activeItem === '/hospitaladmin/userprofile' ? 'active' : ''}>
                <Link to="/hospitaladmin/userprofile" onClick={() => handleMenuClick('/hospitaladmin/userprofile')}>
                  <TbPresentationAnalytics className="nav-icon" />
                  {showText && <span>Profile</span>}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="footersid">
          <div className="footermenu">
            {role !== 'medtinz_client_patient' && (
              <h4 className={activeItem === '/hospitaladmin/generalconfig' ? 'active' : ''}>
                <FaCog /> 
                {showText && <Link to="/hospitaladmin/generalconfig" onClick={() => handleMenuClick('/hospitaladmin/generalconfig')}>Settings</Link>}
              </h4>
            )}
            <h4 onClick={handleLogout} style={{ cursor: 'pointer' }}>
              <FaSignOutAlt /> 
              {showText && <span>Logout</span>}
            </h4>
          </div>
          <div className="footermenu">
            {showText && <img src={logomedtinz} className='cardicon' alt="Consultation Icon" style={{ width: '100px', height: '20px' }} />}
          </div>
          <div className="blueband">
            <div className='boitemed'>
              <div>
                <img src={medocImage} className='medocfooterimg' alt="Consultation Icon" />
              </div>
              <div>
                {showText && role === 'medtinz_client_master' && (
                  <button className="footersidebtn me-auto" onClick={() => navigate("/subscription/upgradeplan")}>Upgrade plan</button>
                )}
              </div>
            </div>
            {showText && <p>The hospital management is facing a shortage of medication</p>}
          </div>
        </div>
      </div>
    </>
  );
}

export default ClientSidebar;